import { useCallback, useEffect, useRef } from 'react';
import {
  AG_UI_STATE_PATCH_EVENT,
  assignPanelsToSiteGroup,
  CANVAS_OPEN_FILE_MANAGER_EVENT,
  getEditor,
  openPanelInCanvas,
  type AgUiStatePatchEventDetail,
} from 'agentable-canvas/whiteboard';
import { useCanvasSession } from '../../contexts/CanvasSessionContext';

const FILE_MANAGER_PANEL_ID = 'file-manager';

/** Opens/refreshes the file-manager panel when agent tools or UI request it. */
export function FileManagerBridge(): null {
  const { session } = useCanvasSession();
  const siteId = session.siteId;
  const refreshRef = useRef(0);

  const refreshFileManager = useCallback(
    (focus: boolean): void => {
      if (!siteId) return;
      refreshRef.current += 1;
      openPanelInCanvas(FILE_MANAGER_PANEL_ID, {
        panelProps: {
          siteId,
          __siteId: siteId,
          refreshKey: refreshRef.current,
          __title: 'File Manager',
        },
        focus,
      });
      const editor = getEditor();
      if (editor) {
        assignPanelsToSiteGroup(editor, [FILE_MANAGER_PANEL_ID], siteId);
      }
    },
    [siteId],
  );

  useEffect(() => {
    if (!siteId) return;
    openPanelInCanvas(FILE_MANAGER_PANEL_ID, {
      panelProps: { siteId, __siteId: siteId, refreshKey: refreshRef.current, __title: 'File Manager' },
      focus: true,
      assignToSiteGroup: false,
    });
    const editor = getEditor();
    if (editor) {
      assignPanelsToSiteGroup(editor, [FILE_MANAGER_PANEL_ID], siteId);
    }
  }, [siteId]);

  useEffect(() => {
    const openHandler = (): void => {
      refreshFileManager(true);
    };
    window.addEventListener(CANVAS_OPEN_FILE_MANAGER_EVENT, openHandler);
    return () => window.removeEventListener(CANVAS_OPEN_FILE_MANAGER_EVENT, openHandler);
  }, [refreshFileManager]);

  useEffect(() => {
    const agUiHandler = (event: Event): void => {
      const detail = (event as CustomEvent<AgUiStatePatchEventDetail>).detail;
      if (!detail?.patches?.length || !siteId) return;
      const filesPatch = detail.patches.find((patch) => patch.path.startsWith('/files'));
      if (!filesPatch) return;
      refreshFileManager(false);
    };
    window.addEventListener(AG_UI_STATE_PATCH_EVENT, agUiHandler);
    return () => window.removeEventListener(AG_UI_STATE_PATCH_EVENT, agUiHandler);
  }, [refreshFileManager, siteId]);

  return null;
}
